"use client";

/**
 * Listing detail panel - a right-side slide-in drawer on desktop and a
 * bottom sheet on mobile (matching the MobileMoreSheet pattern). Opens
 * when a browse card is clicked; the full listing is fetched LIVE from
 * its source (bina.az, ev10.az, emlak.az, or arenda.az - whichever
 * indexed it) via GET /api/listings/detail/{listing_id} (never
 * persisted) and rendered with a photo carousel, key facts, listing
 * number, the description, and the two footer actions (open on the
 * source + full AI analysis in a new tab).
 *
 * Legal posture mirrors the rest of the listings surface: live-fetch
 * only, hotlinked source CDN photos, prominent source attribution +
 * outbound link, and NO seller phone / agent name (the detail query
 * doesn't return contact info and we never add it).
 *
 * Accessibility: role=dialog + aria-modal, focus trap, Escape + overlay
 * + close-button dismissal, body scroll lock while open.
 *
 * This file is the frame: the drawer itself, its bars and actions, the
 * live fetch and the loading skeleton. The content (carousel, facts,
 * residence block, map, seller sections) is ListingDetailBody, a chunk
 * of its own that detail-body-loader fetches on intent, in idle time or
 * with the first open; until it is here a ready listing keeps the
 * skeleton, so a tap still opens the drawer at once.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { upgradeCardImage } from "@/lib/listing-image";
import {
  X, ChevronLeft, ChevronRight, ExternalLink, Zap, Loader2,
  AlertTriangle, CheckCircle2, RefreshCw, Heart, MoreHorizontal, Users,
} from "lucide-react";
import { useT, useLocale } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { fetchListingDetail, ListingRemovedError, formatPrice, currencySign } from "@/lib/api";
import { useSheetSwipe } from "@/lib/use-sheet-drag";
import { useMyPins } from "@/lib/use-my-pins";
import { useWatchlist } from "@/lib/use-watchlist";
import SaveCelebration from "@/components/SaveCelebration";
import { haptic } from "@/lib/haptics";
import { captureEvent, trackEvent } from "@/lib/tracking";
import { withRec, type RecRef } from "@/lib/rec-client";
import { useListingDwell } from "@/lib/use-listing-dwell";
import MyMapEditor from "./MyMapEditor";
import type { ListingDetail } from "@/types";
import { useBackToClose } from "@/lib/use-back-to-close";
import { useHoldInterruptions } from "@/lib/floating-slot";
import { markViewed } from "@/lib/viewed";
import { isSeparate } from "@/lib/property-kinds";
import { flatSellersText } from "@/lib/flat-sellers";
import Photo from "@/components/Photo";
import { useDetailBody } from "./detail-body-loader";


interface Props {
  /** The bina.az external id to fetch live. Null = panel closed. */
  externalId: string | null;
  /** Full source-qualified index id ("bina_az:12345"). When set + the
   *  user is signed in, the drawer shows the My-Map editor (save / status
   *  / notes). Null on surfaces that don't know it (the editor hides). */
  listingId?: string | null;
  /** Outbound source URL from the card - used for the bina.az + analyze
   *  actions so they work even before (or if) the live fetch fails. */
  sourceUrl: string | null;
  /** Card-level coordinates - used as the mini-map fallback before /
   *  if the live detail fetch (which carries its own lat/lng) lands. */
  lat?: number | null;
  lng?: number | null;
  /** Pre-computed AI market gap (signed %, negative = under-priced) from
   *  the card / map point, so the drawer can show the market comparison
   *  without waiting for (or running) a live analysis. */
  overpricedPct?: number | null;
  /** The card's image. Painted under the photo the drawer opens on as
   *  soon as it opens, so the photo box is never a grey pulse while the
   *  live detail loads (the replays showed people rage-tapping that
   *  pulse and the dead arrows on it). */
  thumbnailUrl?: string | null;
  /** The photo the card showed in place of its cover (a photo filter's
   *  match, the floor plan for "plan"): the gallery opens on it. */
  startPhoto?: string | null;
  onClose: () => void;
  /** Layout mode.
   *  - "overlay" (default): the portal drawer/bottom-sheet with a
   *    dimmed backdrop - used on /map where the map IS the page.
   *  - "split": on desktop the panel renders INLINE as a sticky
   *    right-hand pane (the grid shifts left, stays clickable, and
   *    everything transitions back to centre on close); on mobile it
   *    falls back to the overlay bottom sheet. Used on /listings. */
  variant?: "overlay" | "split";
  /** Hide the location mini-map section - the /map page passes false
   *  because the user just clicked the listing ON a map. */
  showMiniMap?: boolean;
  /** True when opened from the My Map (personal) map layer, so the
   *  My-Map editor can drop the redundant "view in My Map" link and
   *  render with a distinct accent. */
  fromMyMap?: boolean;
  /** The card's category, when the caller knows it: a commercial space
   *  or a garage (no valuation) gets no analyze action from the start. */
  category?: string | null;
  /** The recommendation the card came from (its request id) and the
   *  card's position in it: the drawer's events carry them (`rec`, `pos`)
   *  so they join back to the impression (recommender plan 2.2). A card
   *  link carries the same pair as data-rec / data-pos (lib/rec-client). */
  rec?: string | null;
  pos?: number | null;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: ListingDetail }
  | { kind: "removed" }
  | { kind: "error" };


// The docked pane's frame: it sticks 5rem under the top (top-20), keeps
// a 1rem gap above the window's bottom, and never gets shorter than this.
const DOCK_TOP_PX = 80;
const DOCK_GAP_PX = 16;
const DOCK_MIN_PX = 320;
const ANIM_MS = 220;

export default function ListingDetailPanel({
  externalId, listingId, sourceUrl, lat, lng, overpricedPct, thumbnailUrl, startPhoto = null, onClose,
  variant = "overlay", showMiniMap = true, fromMyMap = false, category = null,
  rec = null, pos = null,
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const { user, refreshTokens } = useAuth();

  // A tap on one of the seller's other listings swaps the drawer to
  // that listing IN PLACE (an override over the props). Opening a
  // different card from outside resets the override.
  const [override, setOverride] = useState<{
    externalId: string;
    listingId: string | null;
    sourceUrl: string | null;
  } | null>(null);
  useEffect(() => { setOverride(null); }, [externalId, listingId]);

  const effExternalId = override ? override.externalId : externalId;
  const effListingId = override ? override.listingId : listingId ?? null;
  const effSourceUrl = override ? override.sourceUrl : sourceUrl;

  const open = effExternalId !== null;

  // The composite listing_index id ("ev10_az:12345") the live fetch is
  // keyed on. Most surfaces already know it (listingId); the few that
  // don't predate multi-source and only ever carried bina.az listings,
  // so defaulting the prefix there is correct, not a guess.
  const fetchId = effListingId ?? (effExternalId ? `bina_az:${effExternalId}` : null);

  // The recommendation belongs to the card that opened the drawer, not
  // to a listing swapped in from inside it.
  const effRec = useMemo<RecRef | null>(() => (!override && rec ? { rec, pos } : null), [override, rec, pos]);
  const recRef = useRef(effRec);
  useEffect(() => { recRef.current = effRec; }, [effRec]);
  // Visible time with this listing on screen (`listing.close`).
  useListingDwell(open ? fetchId : null, effRec);
  const gallery = useMemo(() => (fetchId ? { id: fetchId, rec: effRec } : null), [fetchId, effRec]);

  // Split variant docks inline only on >=lg, where there's room for a
  // 520px pane AND a usable grid beside it. Below lg it falls back to
  // the overlay drawer / bottom sheet (same as /map) - docking it on a
  // narrow viewport crushed the grid into 1-2 squashed columns.
  // Tracked as state (not a CSS media query) because the two layouts
  // mount different DOM and we must never mount both (double fetches).
  const [isWide, setIsWide] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const inline = variant === "split" && isWide;

  // Mount/unmount with an exit animation. `render` keeps us in the DOM
  // through the close transition; `entered` drives the transform.
  const [render, setRender] = useState(false);
  const [entered, setEntered] = useState(false);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // The content code (detail-body-loader). Until it is here a ready
  // listing keeps the skeleton; a chunk that cannot be fetched shows the
  // error state, whose retry asks for it again.
  const { body, failed: bodyFailed, retry: retryBody } = useDetailBody(open);
  const shown: LoadState = state.kind !== "ready" || body
    ? state
    : bodyFailed ? { kind: "error" } : { kind: "loading" };
  // URL the user already sent to /analyze (new tab) from this drawer.
  // While it matches the current listing the CTA renders as a
  // disabled "Analyzed" state; opening another card re-enables it
  // because effectiveUrl changes.
  const [analyzedUrl, setAnalyzedUrl] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  // The docked pane fits the window (owner, 2026-10-17: opened from the
  // first row, its bottom and its buttons sat below the window until the
  // page was scrolled): its top follows the list until it sticks under
  // the header, its bottom stays at the window's bottom, and its body
  // scrolls on its own from the start. The height is written straight to
  // the frame on scroll (one read per frame, no re-render).
  const frameRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // ``render`` is false on the first pass (nothing is in the DOM yet),
    // so the fit runs again once the pane has mounted.
    if (!inline || !render) return;
    let raf = 0;
    const fit = () => {
      raf = 0;
      const aside = panelRef.current;
      const frame = frameRef.current;
      if (!aside || !frame) return;
      const top = Math.max(aside.getBoundingClientRect().top, DOCK_TOP_PX);
      frame.style.height = `${Math.max(DOCK_MIN_PX, window.innerHeight - top - DOCK_GAP_PX)}px`;
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(fit); };
    fit();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [inline, render]);
  const lastFocused = useRef<HTMLElement | null>(null);

  // Instagram-style dismissal on phones: a downward drag from ANYWHERE
  // on the sheet (photo, text, top bar) follows the finger while the
  // body is scrolled to its top; a fling or a long pull closes. Inert
  // on the desktop drawer / inline split pane (the hook gates on < sm).
  // `render`, not `open`: the panel element only exists one render
  // after open flips, and the hook binds native listeners to it.
  const { offset: dragOffset, dragging } = useSheetSwipe(panelRef, bodyRef, onClose, open && render);
  // Hardware Back closes this overlay instead of leaving the page.
  useBackToClose(open && !inline, onClose);
  // No offer comes up over an open listing, docked or not (lib/floating-slot).
  useHoldInterruptions(open);


  // Save lives in the top bar as a quiet heart (owner, 2026-09-19: the
  // full-width "save" button pinned above the actions was too pushy).
  // Anonymous visitors get the same heart, routed to sign-in.
  const { signedIn, getPin, save: savePin, remove: removePin } = useMyPins();
  // Signed out, the heart is device-bound (push subscription as the
  // identity, see use-watchlist); the sign-in ask happens on the ladder.
  const deviceWatch = useWatchlist();
  const pin = getPin(effListingId);
  const saved = !!pin;
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveBurst, setSaveBurst] = useState(0);
  useEffect(() => { setSaveBurst(0); }, [effListingId]);
  const toggleSave = useCallback(async () => {
    if (!effListingId || saveBusy) return;
    setSaveBusy(true);
    try {
      if (saved) {
        await removePin(effListingId);
        haptic(4);
      } else {
        await savePin(effListingId);
        setSaveBurst(Date.now());
        haptic(12);
      }
    } catch {
      // The free pin cap raises PinLimitError; use-my-pins opens the
      // global upsell sheet, nothing to do here.
    } finally {
      setSaveBusy(false);
    }
  }, [effListingId, saveBusy, saved, savePin, removePin]);
  // The top bar shows the price only once the body's own price row has
  // scrolled out of view (owner, 2026-09-19: showing it twice at open
  // was pure repetition). Until then it carries the plain title.
  const [priceScrolled, setPriceScrolled] = useState(false);
  useEffect(() => {
    setPriceScrolled(false);
    if (!render || shown.kind !== "ready") return;
    const root = bodyRef.current;
    const anchor = root?.querySelector("[data-price-anchor]");
    if (!root || !anchor) return;
    // Half-visibility, not any-pixel: the desktop drawer's content can
    // be short enough that the row never fully leaves the body.
    const io = new IntersectionObserver(
      ([entry]) => setPriceScrolled(!(entry.isIntersecting && entry.intersectionRatio >= 0.5)),
      { root, threshold: [0, 0.5, 1] },
    );
    io.observe(anchor);
    return () => io.disconnect();
  }, [render, state, shown.kind, fetchId]);

  // Double-tap on the photo saves (never un-saves), like Instagram.
  const saveFromPhoto = useCallback(() => {
    if (!signedIn) return;
    if (!saved) void toggleSave();
    else haptic(6);
  }, [signedIn, saved, toggleSave]);

  // ── Live fetch on open / id change ──────────────────────────────
  useEffect(() => {
    if (fetchId === null) return;
    const ctrl = new AbortController();
    setState({ kind: "loading" });
    // Swapping listings while the pane stays open: read from the top.
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    fetchListingDetail(fetchId, { signal: ctrl.signal })
      .then((data) => {
        setState({ kind: "ready", data });
        // Lead-gen row: WHICH unit the session actually opened, with
        // its complex attribution - the strongest per-complex interest
        // signal we have short of an analysis (B2B, Sea Breeze).
        // The server records the view from this event; the cards update now.
        markViewed(fetchId, data.price ?? null);
        trackEvent("listing.open", withRec({
          id: fetchId,
          src: data.source,
          loc: data.location_name ?? undefined,
          cx: data.complex_slug ?? undefined,
          price: data.price ?? undefined,
          rooms: data.room_count ?? undefined,
          rent: data.leased === true ? 1 : undefined,
          cat: data.category ?? undefined,
          // An office's or a garage's kind: its goal's search profile
          // learns which kind is looked at (services/intent).
          pk: data.property_kind ?? undefined,
        }, recRef.current));
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof ListingRemovedError) setState({ kind: "removed" });
        else setState({ kind: "error" });
      });
    return () => ctrl.abort();
  }, [fetchId]);

  // ── Mount + enter/exit animation ────────────────────────────────
  useEffect(() => {
    if (open) {
      lastFocused.current = (document.activeElement as HTMLElement) ?? null;
      setRender(true);
      return;
    }
    setEntered(false);
    const timer = setTimeout(() => {
      setRender(false);
      // Restore focus to the card that opened the panel, without
      // scrolling the page back to it.
      lastFocused.current?.focus?.({ preventScroll: true });
    }, ANIM_MS);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!render || !open) return;
    const raf = requestAnimationFrame(() => {
      setEntered(true);
      // Move focus into the panel for keyboard + screen-reader users.
      // preventScroll: the docked pane sits below the filters, and a
      // plain focus() scrolled the page ~250 px down for a moment until
      // the grid's keep-place pass scrolled it back (owner, 2026-10-17:
      // "the page kind of jumps").
      panelRef.current?.focus?.({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [render, open]);

  // ── Body scroll lock (overlay only - the inline pane must leave
  //    the page scrollable + clickable, that's its whole point) ─────
  useEffect(() => {
    if (!render || inline) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [render, inline]);

  // ── Escape to close + focus trap (trap is overlay-only: the inline
  //    pane is complementary content, not a modal) ──────────────────
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (inline) return;
      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose, inline]);

  // Prefer the live source_url; fall back to the card's source_url so
  // the actions stay valid even on a failed / pending fetch.
  const liveUrl = state.kind === "ready" ? state.data.source_url : null;
  const effectiveUrl = liveUrl ?? effSourceUrl ?? "";
  const analyzed = !!effectiveUrl && analyzedUrl === effectiveUrl;
  // /analyze auto-runs off ?url= (same entry the PWA share target
  // uses), so auth, the free tier, token spend and rate limiting all
  // apply on that page.
  const analyzeHref = effectiveUrl
    ? `/${locale}/analyze?url=${encodeURIComponent(effectiveUrl)}`
    : null;
  // A commercial space or a garage gets no valuation, so no analyze
  // action: the source link takes the whole row.
  const noValuation = isSeparate(state.kind === "ready" ? state.data.category : (override ? null : category));

  if (!render) return null;

  // Save to My Map: the phone bar's plain heart and the desktop photo chip.
  const saveButton = (cls: string, iconCls: string) => effListingId && (signedIn ? (
    <button
      type="button"
      onClick={() => void toggleSave()}
      disabled={saveBusy}
      aria-pressed={saved}
      aria-label={saved ? t("mymap.remove") : t("mymap.save")}
      title={saved ? t("mymap.saved") : t("mymap.save")}
      className={cls}
    >
      <Heart
        key={saveBurst}
        className={`${iconCls} transition-colors ${
          saved ? "fill-rose-500 text-rose-500" : ""
        } ${saveBurst ? "save-pop" : ""}`}
        strokeWidth={2}
      />
      <SaveCelebration burstKey={saveBurst} color="#f43f5e" praise={t("mymap.praise")} placement="below" />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => {
        if (!deviceWatch.has(effListingId)) {
          setSaveBurst(Date.now());
          haptic(12);
        }
        void deviceWatch.toggle(effListingId);
      }}
      aria-pressed={deviceWatch.has(effListingId)}
      aria-label={deviceWatch.has(effListingId) ? t("mymap.remove") : t("mymap.save")}
      title={deviceWatch.has(effListingId) ? t("mymap.saved") : t("mymap.save")}
      className={cls}
    >
      <Heart
        key={saveBurst}
        className={`${iconCls} transition-colors ${
          deviceWatch.has(effListingId) ? "fill-rose-500 text-rose-500" : ""
        } ${saveBurst ? "save-pop" : ""}`}
        strokeWidth={2}
      />
      <SaveCelebration burstKey={saveBurst} color="#f43f5e" praise={t("mymap.praise")} placement="below" />
    </button>
  ));

  // The "..." menu (share, the listing's page, compare, an alert, the
  // assistant, report; ListingActionsMenu): it comes with the content
  // chunk once the listing is here. Until then a still button holds its
  // place, so the heart beside it never shifts.
  const menuButton = (mode: "sheet" | "popover", cls: string, iconCls: string) => {
    if (!fetchId) return null;
    if (shown.kind === "ready" && body) {
      return (
        <body.ListingActionsMenu
          key={fetchId}
          data={shown.data}
          listingId={fetchId}
          mode={mode}
          triggerClassName={cls}
          iconClassName={iconCls}
          boundaryRef={inline ? frameRef : panelRef}
          onHidden={onClose}
        />
      );
    }
    if (shown.kind !== "loading") return null;
    return (
      <button type="button" disabled aria-label={t("listings.menu.more")} className={cls}>
        <MoreHorizontal className={iconCls} strokeWidth={2} />
      </button>
    );
  };

  // The flat's sellers line (lib/flat-sellers); null for a lone posting.
  const flatSellers = shown.kind === "ready"
    ? flatSellersText(shown.data.flat_sellers, shown.data.leased, locale, t)
    : null;

  // The price and facts a bar shows once the body's own price row has
  // scrolled away (the phone bar, and the desktop bar that replaces the
  // chips over the photo).
  const scrolledSummary = shown.kind === "ready" && shown.data.price !== null ? (
    <>
      <div className="truncate text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
        {formatPrice(shown.data.price)} {currencySign(shown.data.currency) }
      </div>
      <div className="truncate text-xs text-gray-500">
        {[
          shown.data.room_count !== null
            ? t("listings.card.rooms").replace("{n}", String(shown.data.room_count))
            : null,
          shown.data.area_m2 !== null ? `${Math.round(shown.data.area_m2)} m²` : null,
          shown.data.location_name,
        ].filter(Boolean).join(" · ")}
      </div>
    </>
  ) : null;
  const plainIcon = "relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-60";
  // Desktop bar buttons keep one box, gap and icon size in both states,
  // so nothing moves when the bar fills in (owner, 2026-10-17); only the
  // surface fades from a glass chip on the photo to a plain icon. The
  // glass stays light in dark mode, like PHOTO_CHIP.
  const barIcon = `relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60 ${
    priceScrolled
      ? "border-transparent text-gray-700 hover:bg-gray-100"
      : "border-white/70 bg-[rgba(255,255,255,0.92)] text-[#111827] shadow-md backdrop-blur-sm hover:bg-[#ffffff]"
  }`;

  // Shared chrome (header + scrollable body + footer actions) used by
  // both the overlay drawer/sheet and the inline split pane.
  const inner = (
    <>
        {/* Top bar, phones only - stays put while the body scrolls: back
            chevron, price + facts, heart, the "..." menu (report lives in
            it since 2026-10-21). */}
        <div className="flex shrink-0 items-center gap-1 border-b border-gray-100 px-2 py-1.5 pt-[calc(0.375rem+env(safe-area-inset-top))] sm:hidden">
          <button
            type="button"
            onClick={onClose}
            aria-label={t("listings.detail.close")}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-700 active:bg-gray-100 sm:hidden"
          >
            <ChevronLeft className="h-6 w-6" strokeWidth={2.2} />
          </button>
          <div className="relative h-9 min-w-0 flex-1">
            <h2
              className={`absolute inset-0 flex items-center text-sm font-semibold text-gray-900 transition-all duration-200 dark:text-white ${
                priceScrolled ? "-translate-y-1 opacity-0" : "translate-y-0 opacity-100"
              }`}
            >
              {t("listings.detail.title")}
            </h2>
            {shown.kind === "ready" && shown.data.price !== null && (
              <div
                aria-hidden={!priceScrolled}
                className={`absolute inset-0 flex flex-col justify-center transition-all duration-200 ${
                  priceScrolled ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"
                }`}
              >
                {scrolledSummary}
              </div>
            )}
          </div>
          {saveButton(plainIcon, "h-[22px] w-[22px]")}
          {menuButton("sheet", plainIcon, "h-[22px] w-[22px]")}
        </div>

        {/* Desktop: no title bar over the photo (owner, 2026-10-17: "a bit
            too much"). Save, the "..." menu and close sit on the photo as
            glass chips; once the price row scrolls away a solid bar slides
            in with the price, and the chips turn into its plain buttons. */}
        <div
          className={`absolute inset-x-0 top-0 z-20 hidden items-center gap-1 px-3 py-2 transition-[background-color,box-shadow,border-color] duration-200 sm:flex ${
            priceScrolled ? "border-b border-gray-100 bg-white shadow-sm" : "pointer-events-none border-b border-transparent"
          }`}
        >
          <div
            aria-hidden={!priceScrolled}
            className={`min-w-0 flex-1 pl-1 transition-all duration-200 ${priceScrolled ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"}`}
          >
            {scrolledSummary}
          </div>
          <div className="pointer-events-auto flex items-center gap-1">
            {saveButton(barIcon, "h-5 w-5")}
            {menuButton("popover", barIcon, "h-5 w-5")}
            <button
              type="button"
              onClick={onClose}
              aria-label={t("listings.detail.close")}
              className={barIcon}
            >
              <X className="h-5 w-5" strokeWidth={2.2} />
            </button>
          </div>
        </div>

        {/* Body (scrollable) */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto overscroll-contain">
          {shown.kind === "loading" && (
            <DetailSkeleton
              thumbnail={override ? null : upgradeCardImage(thumbnailUrl ?? null) ?? thumbnailUrl ?? null}
              t={t}
            />
          )}
          {shown.kind === "removed" && (
            <DetailMessage
              icon={<AlertTriangle className="w-10 h-10 text-amber-500" />}
              title={t("listings.detail.removed.title")}
              desc={t("listings.detail.removed.desc")}
            />
          )}
          {shown.kind === "error" && (
            <DetailMessage
              icon={<AlertTriangle className="w-10 h-10 text-red-500" />}
              title={t("listings.detail.error.title")}
              desc={t("listings.detail.error.desc")}
              action={
                fetchId
                  ? {
                      label: t("listings.detail.retry"),
                      onClick: () => {
                        retryBody();
                        if (state.kind === "ready") return; // only the content code failed
                        setState({ kind: "loading" });
                        fetchListingDetail(fetchId)
                          .then((data) => setState({ kind: "ready", data }))
                          .catch((err: unknown) =>
                            err instanceof ListingRemovedError
                              ? setState({ kind: "removed" })
                              : setState({ kind: "error" }),
                          );
                      },
                    }
                  : undefined
              }
            />
          )}
          {shown.kind === "ready" && body && (
            <body.DetailContent
              data={shown.data}
              locale={locale}
              t={t}
              placeholder={override ? null : upgradeCardImage(thumbnailUrl ?? null) ?? thumbnailUrl ?? null}
              startPhoto={override ? null : startPhoto}
              fallbackLat={override ? null : lat ?? null}
              fallbackLng={override ? null : lng ?? null}
              overpricedPct={override ? null : overpricedPct ?? null}
              showMiniMap={showMiniMap}
              onPhotoDoubleTap={signedIn && effListingId ? saveFromPhoto : undefined}
              track={gallery}
            />
          )}
          {/* Who has offered this flat across its postings (mandate plan
              2.C.3), the card's badge as a line, right above the other
              postings of the same flat it counts. */}
          {shown.kind === "ready" && body && flatSellers && (
            <p className="flex items-center gap-1.5 px-4 pb-3 text-xs text-gray-500">
              <Users className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
              <span className="font-semibold text-gray-700">{flatSellers}</span>
              <span>· {t("listings.flat_sellers.hint")}</span>
            </p>
          )}
          {shown.kind === "ready" && body && (
            <body.DupGroupSection
              listingId={fetchId}
              t={t}
              onPick={(m) =>
                setOverride({
                  externalId: m.id.includes(":") ? m.id.split(":")[1] : m.id,
                  listingId: m.id,
                  sourceUrl: m.url ?? null,
                })
              }
            />
          )}
          {shown.kind === "ready" && body && (
            <body.SellerSection
              data={shown.data}
              currentId={fetchId}
              t={t}
              onPick={(m) =>
                setOverride({
                  externalId: m.external_id ?? m.id.split(":")[1] ?? m.id,
                  listingId: m.id,
                  sourceUrl: m.source_url ?? null,
                })
              }
            />
          )}
          {shown.kind === "ready" && body && shown.data.source === "own" && (
            <body.OwnContactSection data={shown.data} t={t} rec={effRec} />
          )}
          {/* The tabs under the listing (feedback ticket 65): the other
              flats of its residence, the nearby ones, the lookalikes of
              its market; fetched when scrolled into view, a tap swaps the
              drawer to that listing like the seller's other listings. */}
          {shown.kind === "ready" && body && fetchId && (
            <div className="px-4 pb-4 sm:px-5">
              <body.ListingAroundSection
                listingId={fetchId}
                data={shown.data}
                t={t}
                onPick={(m) =>
                  setOverride({
                    externalId: m.external_id ?? m.id.split(":")[1] ?? m.id,
                    listingId: m.id,
                    sourceUrl: m.source_url ?? null,
                  })
                }
              />
            </div>
          )}
          {/* The report action lives in the top bar's "..." menu
              (2026-10-21; before that a flag icon there, owner 2026-09-19:
              "move it to the top or minimize it"). */}
        </div>

        {/* My Map - save / status / notes (signed-in + listingId only;
            the component self-hides otherwise). Sits above the actions so
            it's reachable without scrolling the body. */}
        <MyMapEditor listingId={effListingId} onMyMapLayer={fromMyMap} />

        {/* Footer actions. The analyze CTA opens the full /analyze page
            in a NEW TAB (auto-runs off ?url=), then flips to a disabled
            "Analyzed" state for this listing so a second click can't
            run it again by accident. Opening a different card
            re-enables it (effectiveUrl changes). */}
        <div className="shrink-0 border-t border-gray-100 px-4 py-2 sm:py-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))] sm:pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex gap-2">
          {noValuation ? null : analyzed ? (
            <button
              type="button"
              disabled
              className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-indigo-400 py-2.5 text-sm font-semibold text-white cursor-not-allowed"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              {t("listings.card.cta.analyzed")}
            </button>
          ) : (
            <a
              href={analyzeHref ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!analyzeHref}
              onClick={(e) => {
                if (!analyzeHref) {
                  e.preventDefault();
                  return;
                }
                setAnalyzedUrl(effectiveUrl);
              }}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-white transition-colors ${
                analyzeHref
                  ? "bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800"
                  : "bg-indigo-400 cursor-not-allowed"
              }`}
            >
              <Zap className="w-4 h-4 shrink-0" />
              {t("listings.card.cta.analyze")}
            </a>
          )}
          <a
            href={effectiveUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            // The source is the contact path for an indexed listing: a
            // contact label of its own beside the outbound.click the
            // delegated listener records (recommender plan 2.2).
            onClick={() => {
              if (effectiveUrl && fetchId) captureEvent("listing.contact.click", withRec({ id: fetchId, kind: "source" }, effRec));
            }}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
            <ExternalLink className="w-4 h-4 shrink-0" />
            {/* One word, no site name: "yeniemlak.az-da baxın" no longer
                fit beside the analyse button on phones, and "Əlaqə
                saxlayın" was still long (owner, 2026-10-18); the host
                stays on the photo's attribution chip. */}
            {t("listings.card.cta.view")}
          </a>
        </div>
    </>
  );

  if (inline) {
    // ── Split pane (desktop /listings) ────────────────────────────
    // Plain in-flow flex sibling of the grid: no portal, no backdrop,
    // the grid stays clickable so swapping listings is one click. The
    // width/margin transition slides the content back to centre on
    // close; the fixed-width inner wrapper keeps the content from
    // reflowing mid-animation.
    return (
      <aside
        ref={panelRef}
        tabIndex={-1}
        aria-label={t("listings.detail.title")}
        // The shell reserves its full width the moment it mounts (and
        // releases it in one step on unmount) - animating the WIDTH
        // made the auto-fill grid recompute columns on every frame of
        // the transition, a 200 ms storm of card resizes. With the
        // space reserved up front the grid reflows exactly once, and
        // the card itself slides in over the empty slot via transform
        // (compositor-only, no layout per frame).
        className="hidden lg:block shrink-0 sticky top-20 self-start w-[520px] ml-5"
      >
        <div
          ref={frameRef}
          // Fixed height (not max-h): the loading skeleton is shorter than
          // the loaded content, so a content-driven height made the pane
          // shrink and regrow on every listing swap. The height is the
          // window's room below the pane's top (set on scroll above); the
          // class is the first paint's value.
          className={`relative w-[520px] h-[calc(100vh-6rem)] flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5 transition-[translate,transform,opacity] duration-200 ease-out ${
            entered ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"
          }`}
        >
          {inner}
        </div>
      </aside>
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1200]"
      role="dialog"
      aria-modal="true"
      aria-label={t("listings.detail.title")}
    >
      {/* Overlay */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          // Thins out as the sheet is pulled down so the page shows
          // through under the finger.
          opacity: entered ? Math.max(0, 1 - dragOffset / 480) : 0,
          transition: dragging ? "none" : `opacity ${ANIM_MS}ms ease-out`,
        }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      {/* Panel: bottom sheet on mobile, right drawer on >= sm.
          Mobile  -> pinned to bottom, rounded top, slides up.
          Desktop -> pinned to right, full height, slides in from right. */}
      <div
        ref={panelRef}
        tabIndex={-1}
        style={{
          // Tailwind v4's translate-* utilities set the CSS `translate`
          // property (not `transform`) - transitioning only transform
          // made the sheet/drawer snap into place with no animation.
          transition: dragging
            ? "none"
            : `translate ${ANIM_MS}ms ease-out, transform ${ANIM_MS}ms ease-out`,
          // Drag offset rides on `transform`, composing with the
          // class-driven `translate` enter/exit position.
          transform: `translateY(${dragOffset}px)`,
        }}
        className={[
          "absolute outline-none bg-white shadow-2xl flex flex-col",
          // Mobile: a FULL-SCREEN view (Instagram post style). inset-0
          // pins it to the visible viewport whatever the browser chrome
          // does, so the top bar is always the first thing on screen -
          // no more vh arithmetic and no more "bleed". Corners round
          // only while it is being pulled down.
          "inset-0",
          dragOffset > 0 ? "rounded-t-2xl" : "rounded-none",
          // Desktop right drawer
          "sm:inset-y-0 sm:right-0 sm:left-auto sm:bottom-auto sm:top-0",
          "sm:h-full sm:max-h-none sm:w-[480px] sm:max-w-[92vw] sm:rounded-none sm:rounded-l-2xl sm:overflow-hidden",
          // Enter/exit transforms differ per breakpoint - drive both
          // via translate utilities gated on `entered`.
          entered
            ? "translate-y-0 sm:translate-x-0"
            : "translate-y-full sm:translate-y-0 sm:translate-x-full",
        ].join(" ")}
      >
        {inner}
      </div>
    </div>,
    document.body,
  );
}

/* ── Skeleton + message states ────────────────────────────────────── */

function DetailSkeleton({
  thumbnail,
  t,
}: {
  thumbnail: string | null;
  t: ReturnType<typeof useT>;
}) {
  // gray-300 (not gray-200): on the dark panel the inverted scale puts
  // gray-200 at ~0.26, almost the 0.22 surface tone, so the skeleton was
  // invisible in dark mode (read as "no loading, just lag"). gray-300
  // (~0.35 dark / a clear gray on white) stays visible in both themes.
  //
  // The photo box is NOT part of the pulse when the card gave us its
  // thumbnail: the real first photo shows at once, with the arrows
  // present but disabled, so a tap during the load reads as "one
  // moment" instead of "broken".
  return (
    <div>
      {thumbnail ? (
        <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden select-none">
          <Photo src={thumbnail} alt="" loading="eager" />
          <button
            type="button"
            disabled
            aria-label={t("listings.detail.prev_photo")}
            className="absolute left-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-black/45 text-white opacity-50 cursor-wait"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            disabled
            aria-label={t("listings.detail.next_photo")}
            className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-black/45 text-white opacity-50 cursor-wait"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-black/55 text-white text-xs font-medium px-2 py-0.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            {t("listings.detail.loading")}
          </div>
        </div>
      ) : (
        <div className="aspect-[4/3] bg-gray-300 animate-pulse" />
      )}
      <div className="p-4 space-y-4 animate-pulse">
        <div className="h-7 w-1/2 rounded bg-gray-300" />
        <div className="h-4 w-2/3 rounded bg-gray-300" />
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 rounded bg-gray-300" />
          ))}
        </div>
        <div className="space-y-2">
          <div className="h-3 w-full rounded bg-gray-300" />
          <div className="h-3 w-11/12 rounded bg-gray-300" />
          <div className="h-3 w-3/4 rounded bg-gray-300" />
        </div>
      </div>
    </div>
  );
}

function DetailMessage({
  icon,
  title,
  desc,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-3 px-6 py-20">
      {icon}
      <p className="text-base font-semibold text-gray-800">{title}</p>
      <p className="text-sm text-gray-500 max-w-xs">{desc}</p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-1 flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {action.label}
        </button>
      )}
    </div>
  );
}
